import { FlashList, ListRenderItemInfo } from '@shopify/flash-list';
import React, { useCallback, useLayoutEffect, useMemo } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import Animated, {
  FadeIn,
  FadeOut,
  SlideInLeft,
  SlideInRight,
  SlideOutLeft,
  SlideOutRight,
} from 'react-native-reanimated';

import { ActionSheet, Button, IconStarRateFill, Text, tokens } from '@fhs/ui';
import { ICartEntry } from '@rbi-ctg/menu';

import {
  useEditStepSelectedEntry,
  useHandleOfferItemSelection,
  useOfferCart,
  useSelectedStep,
} from '../../state/offer-guide-cart-state';

import { BottomButtonContainer } from './bottom-button';
import { ItemTile, ItemTileProps } from './item-tile';
import { SelectedEligibleItem } from './selected-eligible-item';
import { SummaryStepItem } from './summary-step-item';

const StepView = Platform.select({
  native: ({ children, isGoingForward = true, skipEntering = false }) => (
    <Animated.View
      style={{ flex: 1 }}
      entering={skipEntering ? undefined : isGoingForward ? SlideInRight : SlideInLeft}
      exiting={isGoingForward ? SlideOutLeft : SlideOutRight}
    >
      {children}
    </Animated.View>
  ),
  web: ({ children }) => <View style={{ flex: 1 }}>{children}</View>,
}) as any;

const ContentView = Platform.select({
  native: ({ children, style = {} }) => (
    <Animated.View style={style} entering={FadeIn.duration(500)} exiting={FadeOut}>
      {children}
    </Animated.View>
  ),
  web: ({ children, style = {} }) => <View style={style}>{children}</View>,
}) as any;

const numColumns = 2;
const renderItem = ({ item }: ListRenderItemInfo<ItemTileProps>) => {
  return <ItemTile {...item} />;
};

export function Content({ isFirstRender }: { isFirstRender: boolean }) {
  const {
    currentStepIndex,
    isGoingForward,
    selectedEligibleItem,
    setSelectedEligibleItem,
    goToNextNotDoneStep,
  } = useOfferCart();
  const { editStepSelectedEntry } = useEditStepSelectedEntry();
  const handleOfferItemSelection = useHandleOfferItemSelection();

  const pointerEvents =
    selectedEligibleItem && !selectedEligibleItem.requiresCustomization ? 'none' : 'auto';

  const selectedStep = useSelectedStep();

  const hasSelectedEntry = !!selectedStep?.selectedEntry;
  const selectedEntryEligibleItem = selectedStep?.eligibleItems.find(
    eligibleItem => eligibleItem.id === selectedStep.selectedEntry?._id
  );

  const eligibleItemsFormatted = useMemo(
    () =>
      selectedStep?.eligibleItems?.map<ItemTileProps>((eligibleItem, index) => ({
        productListTileProps: {
          size: 'lg',
          title: eligibleItem.displayName,
          description: eligibleItem.descriptionShort,
          badge: eligibleItem.badge ?? undefined,
          image: {
            asset: {
              uri: eligibleItem.image?.asset?.uri,
              blurHash: eligibleItem.image?.asset?.blurHash ?? undefined,
            },
            altText: eligibleItem.image?.altText,
          },
          isAvailable: eligibleItem.isAvailable,
          onPress: () => setSelectedEligibleItem(eligibleItem),
        },
        id: eligibleItem.id,
        isFirstColumn: index % numColumns === 0,
        isLastColumn: index % numColumns === numColumns - 1,
        selectedEligibleItem,
        slug: eligibleItem.slug,
        addItemToSimplyOfferCart: (cartEntry: ICartEntry) =>
          handleOfferItemSelection(currentStepIndex, cartEntry),
      })),
    [
      selectedStep?.eligibleItems,
      setSelectedEligibleItem,
      currentStepIndex,
      selectedEligibleItem,
      handleOfferItemSelection,
    ]
  );

  useLayoutEffect(() => {
    if (
      selectedStep?.eligibleItems?.length === 1 &&
      selectedStep.eligibleItems[0].requiresCustomization
    ) {
      // When auto selecting an eligible item when there is only one, it's better to consider it as an edit
      setSelectedEligibleItem(selectedStep.eligibleItems[0], true);
    }
  }, [selectedStep?.eligibleItems, setSelectedEligibleItem]);

  const onPressCustomize = useCallback(() => {
    editStepSelectedEntry(currentStepIndex);
  }, [currentStepIndex, editStepSelectedEntry]);

  return (
    <>
      {!selectedEligibleItem && !hasSelectedEntry && (
        <View style={styles.contentHeader} pointerEvents={pointerEvents}>
          {selectedStep?.points !== undefined && selectedStep?.points !== null && (
            <View style={styles.pointsContainer}>
              <IconStarRateFill color={tokens.colors.$houseYellow} />
              <Text.Ui size="sm" weight="semibold">
                {selectedStep?.points.toLocaleString()} points
              </Text.Ui>
            </View>
          )}
          {selectedStep?.title && <Text.Ui size="sm">{selectedStep?.title}</Text.Ui>}
          {selectedStep?.subtitle && (
            <Text.Heading type="three">{selectedStep?.subtitle}</Text.Heading>
          )}
        </View>
      )}
      <StepView key={currentStepIndex} isGoingForward={isGoingForward} skipEntering={isFirstRender}>
        <View style={{ flex: 1 }} pointerEvents={pointerEvents}>
          {selectedEligibleItem && selectedEligibleItem.requiresCustomization ? (
            <SelectedEligibleItem selectedEligibleItem={selectedEligibleItem} />
          ) : (
            <ContentView style={styles.content}>
              {hasSelectedEntry && (
                <View style={{ paddingTop: 12 }}>
                  <Text.Ui size="md" weight="semibold">
                    Customize Your Selection
                  </Text.Ui>
                  <SummaryStepItem
                    buttonLabel="Customize"
                    style={styles.summaryStepItem}
                    selectedEntry={selectedStep.selectedEntry}
                    selectedEntryImage={selectedStep.selectedEntryImage}
                    onPressButton={onPressCustomize}
                    hideButton={
                      selectedEntryEligibleItem && !selectedEntryEligibleItem.requiresCustomization
                    }
                  />
                  <View style={styles.dividerContainer}>
                    <View style={styles.dividerLine} />
                    <Text.Ui size="md">or</Text.Ui>
                    <View style={styles.dividerLine} />
                  </View>
                  <Text.Ui style={{ paddingTop: 16 }} size="md" weight="semibold">
                    Replace Your Selection:
                  </Text.Ui>
                </View>
              )}
              <FlashList
                showsVerticalScrollIndicator={false}
                data={eligibleItemsFormatted}
                renderItem={renderItem}
                numColumns={numColumns}
                renderScrollComponent={ActionSheet.ScrollView}
              />
              {hasSelectedEntry && (
                <View style={{ marginHorizontal: -16 }}>
                  <BottomButtonContainer>
                    <Button size="xl" onPress={goToNextNotDoneStep}>
                      <Button.Text>Skip</Button.Text>
                    </Button>
                  </BottomButtonContainer>
                </View>
              )}
            </ContentView>
          )}
        </View>
      </StepView>
    </>
  );
}

const styles = StyleSheet.create({
  contentHeader: {
    alignItems: 'center',
    gap: 4,
  },
  pointsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  summaryStepItem: {
    borderWidth: 1,
    borderColor: tokens.colors.$blackOpacity10,
    marginTop: 12,
    borderRadius: 8,
  },
  dividerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginTop: 16,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: tokens.colors.$blackOpacity10,
  },
  content: {
    flex: 1,
    paddingHorizontal: 16,
  },
});
